import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DateTime } from 'luxon'

import { useAppDispatch } from '@/utils/typeHelpers'
import {
  ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY,
  fetchUserTaxEstimates,
} from './userTaxEstimates.slice'
import { QuarterlyTaxPaymentHistory } from './components/QuarterlyTaxPaymentHistory'
import { useAnalyticsView } from '@/features/amplitude'
import { useReselector } from '@/utils/sharedHooks'
import { PageHeader } from '@/components/PageHeader'
import { SmsConsentButton } from '@/features/user/settings/sections/account-details-card/sms-consent/SmsConsentButton'
import { ActiveQuarterTab } from './components/ActiveQuarterTab'
import { getIsFetchingOrNotStarted } from '@/reducers/fetch'
import { fetchFilteredUserTransactions } from '@/features/transactions/transactions.slice'
import { fetchBookkeepingReports } from '@/actions/bookkeepingReportActions'

import {
  FETCH_ALL_USER_TAX_CALCULATIONS_KEY,
  fetchUserTaxCalculationsIfNeeded,
} from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'
import {
  FETCH_ALL_QUARTERLY_TAX_ESTIMATION_DETAILS_KEY,
  fetchAllQuarterlyTaxEstimateDetailsIfNeeded,
} from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import {
  selectActiveQTEDetailsAsDates,
  selectActiveQuarterlyTaxEstimateDetails,
} from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
import { Tab, Loader } from '@/ui'
import { selectUserHasPermission } from '@/reducers/auth/permission.selectors'
import { ProductFeatureId } from '@/actions/permissionActions'

const TAB_INDEXES: { [key: number]: string } = {
  0: 'current',
  1: 'payment_history',
}

export const QuarterlyTaxEstimatesPanel = () => {
  const dispatch = useAppDispatch()
  const fetchingEstimates = useReselector(
    getIsFetchingOrNotStarted,
    ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY
  )
  const fetchingQTEDetails = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_ALL_QUARTERLY_TAX_ESTIMATION_DETAILS_KEY
  )
  const fetchingUserTaxCalculations = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_ALL_USER_TAX_CALCULATIONS_KEY
  )
  const fetching =
    fetchingEstimates || fetchingQTEDetails || fetchingUserTaxCalculations
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTabIndex, setActiveTabIndex] = useState(
    searchParams.get('tab') === 'payment_history' ? 1 : 0
  )
  const activeQuarterlyTaxEstimateDetails = useReselector(
    selectActiveQuarterlyTaxEstimateDetails
  )
  const activeQTEDetails = useReselector(selectActiveQTEDetailsAsDates)

  const pageView = useAnalyticsView()

  const booksStart =
    activeQTEDetails?.utcNoOffset.bookkeepingPeriodStartDate.toISODate()
  const booksEnd =
    activeQTEDetails?.utcNoOffset.bookkeepingPeriodEndDate.toISODate()
  const { startDate, endDate } = useMemo(
    () => ({
      startDate: booksStart ? DateTime.fromISO(booksStart) : undefined,
      endDate: booksEnd ? DateTime.fromISO(booksEnd) : undefined,
    }),
    [booksEnd, booksStart]
  )

  useEffect(() => {
    dispatch(fetchUserTaxEstimates())
    dispatch(fetchAllQuarterlyTaxEstimateDetailsIfNeeded())
    dispatch(fetchUserTaxCalculationsIfNeeded(true))
    dispatch(fetchFilteredUserTransactions({ startDate, endDate }))
    dispatch(fetchBookkeepingReports())
  }, [dispatch, endDate, startDate])

  useEffect(() => {
    pageView('quarterly taxes')
  }, [pageView])

  const tabs = useMemo(() => {
    const panes: {
      menuItem: string
      render: () => JSX.Element | null
    }[] = [
      {
        menuItem: `Q${activeQuarterlyTaxEstimateDetails?.taxQuarter} ${activeQuarterlyTaxEstimateDetails?.taxYear}`,
        render: () => <ActiveQuarterTab />,
      },
    ]

    if (activeQuarterlyTaxEstimateDetails) {
      panes.push({
        menuItem: 'Payment History',
        render: () => <QuarterlyTaxPaymentHistory />,
      })
    }

    return panes
  }, [activeQuarterlyTaxEstimateDetails])

  const hasQTEPermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.quarterlyTaxes
  )

  const innerContent = useMemo(() => {
    if (!hasQTEPermission) {
      return <ActiveQuarterTab />
    }
    const updateTab = (newIndex: number) => {
      setSearchParams({ tab: TAB_INDEXES[newIndex] }, { replace: true })
      setActiveTabIndex(newIndex)
    }
    return (
      <Tab
        panes={tabs}
        defaultActiveIndex={activeTabIndex}
        onTabClick={updateTab}
        variant="outline"
      />
    )
  }, [hasQTEPermission, tabs, activeTabIndex, setSearchParams])

  return (
    <>
      <PageHeader
        header="Quarterly Taxes"
        extraContent={<SmsConsentButton />}
      />
      <br />
      {fetching ? <Loader active={fetching} /> : innerContent}
    </>
  )
}
